import React, { useState } from 'react';
import {
  BarChart3,
  FileText,
  CheckSquare,
  Mail,
  ArrowLeft,
  TrendingUp,
  AlertTriangle,
  Clock,
  Users,
  Star,
  Copy,
  ExternalLink,
  Download,
  MessageSquare,
  Database
} from 'lucide-react';
import clsx from 'clsx';
import { ResultsSectionProps } from '../types/analysis';
import { 
  getPriorityMapping, 
  getCategoryMapping,
  getFormalityMapping,
  getRelationshipMapping,
  getToneMapping
} from '../constants/mappings';
import { useSalesStageMapping } from '../hooks/useSalesStageMapping';

type TabType = 'feedback' | 'summary' | 'todo' | 'email' | 'transcript' | 'additional';

const TABS = [
  { id: 'feedback' as TabType, label: 'Analiza Sprzedaży', icon: BarChart3 },
  { id: 'summary' as TabType, label: 'Podsumowanie', icon: FileText },
  { id: 'todo' as TabType, label: 'Lista Zadań', icon: CheckSquare },
  { id: 'email' as TabType, label: 'Email Follow-up', icon: Mail },
  { id: 'transcript' as TabType, label: 'Transkrypt', icon: MessageSquare },
  { id: 'additional' as TabType, label: 'Dane CRM', icon: Database },
];

// Helper function to get speaker display info
const getSpeakerInfo = (speakerKey: string, index: number, total: number) => {
  // Define colors for up to 6 speakers
  const colors = [
    'bg-primary-500',
    'bg-secondary-500', 
    'bg-green-500',
    'bg-purple-500',
    'bg-orange-500',
    'bg-pink-500'
  ];

  // Define icons/emojis for speakers
  const icons = ['🎙️', '👤', '💼', '🧑‍💼', '👩‍💼', '🎯'];
  
  // Try to determine speaker role from name
  let displayName = speakerKey;
  let icon = icons[index % icons.length];
  
  // Smart mapping for common speaker names
  if (speakerKey.toLowerCase().includes('prowadz') || speakerKey.toLowerCase().includes('sprzed') || 
      speakerKey.toLowerCase().includes('konsult') || speakerKey === 'SPEAKER_A') {
    displayName = 'Prowadzący';
    icon = '🎙️';
  } else if (speakerKey.toLowerCase().includes('klient') || speakerKey.toLowerCase().includes('client') ||
             (speakerKey === 'SPEAKER_B' && total === 2)) {
    displayName = 'Klient';
    icon = '👤';
  } else if (speakerKey.match(/^SPEAKER_[A-Z]$/)) {
    // For generic SPEAKER_X format, create friendly names
    const letterIndex = speakerKey.charCodeAt(speakerKey.length - 1) - 65; // A=0, B=1, etc.
    if (letterIndex === 0 && total > 1) {
      displayName = 'Prowadzący';
      icon = '🎙️';
    } else if (letterIndex === 1 && total === 2) {
      displayName = 'Klient';
      icon = '👤';
    } else {
      displayName = `Uczestnik ${String.fromCharCode(65 + letterIndex)}`;
      icon = icons[letterIndex % icons.length];
    }
  } else if (speakerKey.match(/^Speaker\s*[A-Z]?$/i)) {
    // Handle "Speaker", "Speaker A", etc.
    displayName = total === 2 && index === 1 ? 'Klient' : `Uczestnik ${index + 1}`;
  } else {
    // For custom names, keep them but add numbering if needed
    displayName = total > 2 ? `${speakerKey} (${index + 1})` : speakerKey;
  }
  
  return {
    displayName,
    icon,
    color: colors[index % colors.length]
  };
};

export const ResultsSection: React.FC<ResultsSectionProps> = ({ results, onReset, showPermalink = false }) => {
  const [activeTab, setActiveTab] = useState<TabType>('feedback');
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const [fullTranscript, setFullTranscript] = useState<string | null>(null);
  const [loadingTranscript, setLoadingTranscript] = useState<boolean>(false);
  const [transcriptCopied, setTranscriptCopied] = useState<boolean>(false);
  
  // Dynamic sales stage mapping
  const salesStage = results.metadata?.salesStage || '';
  const { mapping: salesStageMapping } = useSalesStageMapping(salesStage);
  const emailSalesStage = results.email?.communicationAnalysis?.salesStage || '';
  const { mapping: emailSalesStageMapping } = useSalesStageMapping(emailSalesStage);
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const copyPermalink = () => {
    if (results.analysisId) {
      const permalink = `${window.location.origin}/analysis/${results.analysisId}`;
      copyToClipboard(permalink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000); // Reset after 2 seconds
    }
  };

  // Load full transcript when transcript tab is accessed
  React.useEffect(() => {
    const loadFullTranscript = async () => {
      const transcript = results.transcript;
      if (!transcript?.fullText && !transcript?.sessionId) return;
      
      try {
        setLoadingTranscript(true);
        let transcriptContent = '';
        
        // If we have fullText (from permalink/database), use it directly
        if (transcript.fullText) {
          transcriptContent = transcript.fullText;
        } 
        // Otherwise try to fetch from API using sessionId (for fresh analyses)
        else if (transcript.sessionId) {
          const response = await fetch(`/api/transcript/${transcript.sessionId}`);
          
          if (!response.ok) {
            throw new Error(`Load failed: ${response.status} ${response.statusText}`);
          }
          
          transcriptContent = await response.text();
        }
        
        setFullTranscript(transcriptContent);
        
      } catch (error) {
        console.error('Load failed:', error);
        setFullTranscript(transcript?.preview || 'Nie udało się załadować transkryptu');
      } finally {
        setLoadingTranscript(false);
      }
    };
    
    if (activeTab === 'transcript') {
      loadFullTranscript();
    }
  }, [activeTab, results.transcript?.fullText, results.transcript?.sessionId, results.transcript?.preview]);
  
  const copyFullTranscript = () => {
    const textToCopy = fullTranscript || results.transcript?.preview || '';
    if (textToCopy) {
      copyToClipboard(textToCopy);
      setTranscriptCopied(true);
      setTimeout(() => setTranscriptCopied(false), 2000); // Reset after 2 seconds
    } else {
      alert('Transkrypt niedostępny do skopiowania');
    }
  };

  const renderFeedbackTab = () => (
    <div className="space-y-6">
      {/* Overall Score */}
{/*      <div className="glass-card rounded-2xl p-6">*/}
{/*        <div className="flex items-center justify-between mb-4">*/}
{/*          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">*/}
{/*            Ogólna wydajność*/}
{/*          </h3>*/}
{/*          <div className="flex items-center space-x-2">*/}
{/*            <Star className="w-6 h-6 text-accent-500" />*/}
{/*            <span className="text-3xl font-bold gradient-text">*/}
{/*{typeof results.feedback?.overallScore === 'string' && results.feedback.overallScore.includes('/10') */}
{/*                ? results.feedback.overallScore */}
{/*                : `${results.feedback?.overallScore || 0}/10`}*/}
{/*            </span>*/}
{/*          </div>*/}
{/*        </div>*/}
{/*        */}
{/*        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl h-4 overflow-hidden">*/}
{/*          <div */}
{/*            className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl transition-all duration-1000"*/}
{/*            style={{ width: `${((parseFloat(typeof results.feedback?.overallScore === 'string' && results.feedback.overallScore.includes('/10') */}
{/*              ? results.feedback.overallScore.split('/')[0] */}
{/*              : results.feedback?.overallScore) || 0) / 10) * 100}%` }}*/}
{/*          ></div>*/}
{/*        </div>*/}
{/*      </div>*/}

      {/* Detailed Questioning Analysis */}
      {/*{results.feedback?.questioningAnalysis && (*/}
      {/*  <div className="glass-card rounded-2xl p-6">*/}
      {/*    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-6">*/}
      {/*      Szczegółowa analiza zadawania pytań*/}
      {/*    </h3>*/}
      {/*    <div className="grid md:grid-cols-2 gap-6">*/}
      {/*      /!* Quantitative Metrics *!/*/}
      {/*      <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20 rounded-xl p-6">*/}
      {/*        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-4">Metryki ilościowe</h4>*/}
      {/*        <div className="space-y-2 text-sm">*/}
      {/*          <div className="flex justify-between">*/}
      {/*            <span className="text-blue-700 dark:text-blue-300">Łącznie pytań:</span>*/}
      {/*            <span className="font-medium">{results.feedback.questioningAnalysis.quantitativeMetrics?.totalQuestions || 'N/A'}</span>*/}
      {/*          </div>*/}
      {/*          <div className="flex justify-between">*/}
      {/*            <span className="text-blue-700 dark:text-blue-300">Pytania otwarte:</span>*/}
      {/*            <span className="font-medium">{results.feedback.questioningAnalysis.quantitativeMetrics?.openQuestions || 'N/A'}</span>*/}
      {/*          </div>*/}
      {/*          <div className="flex justify-between">*/}
      {/*            <span className="text-blue-700 dark:text-blue-300">Pytania zamknięte:</span>*/}
      {/*            <span className="font-medium">{results.feedback.questioningAnalysis.quantitativeMetrics?.closedQuestions || 'N/A'}</span>*/}
      {/*          </div>*/}
      {/*          <div className="flex justify-between">*/}
      {/*            <span className="text-blue-700 dark:text-blue-300">Pytania uzupełniające:</span>*/}
      {/*            <span className="font-medium">{results.feedback.questioningAnalysis.quantitativeMetrics?.followUpQuestions || 'N/A'}</span>*/}
      {/*          </div>*/}
      {/*        </div>*/}
      {/*      </div>*/}

      {/*      /!* Best Question *!/*/}
      {/*      <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20 rounded-xl p-6">*/}
      {/*        <h4 className="font-semibold text-green-800 dark:text-green-200 mb-4">Najlepsze pytanie</h4>*/}
      {/*        <p className="text-green-800 dark:text-green-200 font-medium">*/}
      {/*          {results.feedback.questioningAnalysis.bestQuestion?.justification || 'Brak dostępnych danych'}*/}
      {/*        </p>*/}
      {/*        <div className="mt-3 p-3 bg-green-100 dark:bg-green-900/30 border-l-4 border-green-500 rounded-r-lg">*/}
      {/*          <p className="text-green-700 dark:text-green-300 text-sm font-medium italic">*/}
      {/*            &quot;{results.feedback.questioningAnalysis.bestQuestion?.quote || 'Brak dostępnego cytatu'}&quot;*/}
      {/*          </p>*/}
      {/*        </div>*/}
      {/*      </div>*/}
      {/*    </div>*/}

      {/*    /!* Category Scores *!/*/}
      {/*    <div className="mt-6">*/}
      {/*      <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">Oceny kategorii</h4>*/}
      {/*      <div className="space-y-3">*/}
      {/*        {(results.feedback.questioningAnalysis.categoryScores || []).map((category, index) => (*/}
      {/*          <div key={index} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">*/}
      {/*            <div className="flex justify-between items-center">*/}
      {/*              <p className="text-gray-800 dark:text-gray-200 font-medium">{category.category}</p>*/}
      {/*              <span className="text-xl font-bold text-gray-600 dark:text-gray-400">*/}
      {/*                {typeof category.score === 'string' && category.score.includes('/10') */}
      {/*                  ? category.score */}
      {/*                  : `${category.score}/10`}*/}
      {/*              </span>*/}
      {/*            </div>*/}
      {/*            <p className="text-gray-600 dark:text-gray-400 text-xs mt-2 italic">*/}
      {/*              {category.examples}*/}
      {/*            </p>*/}
      {/*          </div>*/}
      {/*        ))}*/}
      {/*      </div>*/}
      {/*    </div>*/}

      {/*    /!* Quality Criteria *!/*/}
      {/*    <div className="mt-6">*/}
      {/*      <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">Kryteria jakości</h4>*/}
      {/*      <div className="space-y-3">*/}
      {/*        {(results.feedback.questioningAnalysis.qualityCriteria || []).map((criteria, index) => (*/}
      {/*          <div key={index} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">*/}
      {/*            <div className="flex justify-between items-center">*/}
      {/*              <p className="text-gray-800 dark:text-gray-200 font-medium">{criteria.area}</p>*/}
      {/*              <span className="text-xl font-bold text-gray-600 dark:text-gray-400">*/}
      {/*                {typeof criteria.score === 'string' && criteria.score.includes('/10') */}
      {/*                  ? criteria.score */}
      {/*                  : `${criteria.score}/10`}*/}
      {/*              </span>*/}
      {/*            </div>*/}
      {/*            <p className="text-gray-600 dark:text-gray-400 text-xs mt-2 italic">*/}
      {/*              {criteria.comment}*/}
      {/*            </p>*/}
      {/*          </div>*/}
      {/*        ))}*/}
      {/*      </div>*/}
      {/*    </div>*/}

      {/*    /!* Main Improvement *!/*/}
      {/*    <div className="mt-6">*/}
      {/*      <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">Główny obszar do poprawy</h4>*/}
      {/*      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">*/}
      {/*        <p className="text-amber-800 dark:text-amber-200 font-medium">*/}
      {/*          {results.feedback.questioningAnalysis.mainImprovement || 'Brak dostępnych sugestii popraw'}*/}
      {/*        </p>*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*)}*/}

      {/* Strengths & Improvements Grid */}
      {results.analysisId === '280b2716-f751-4b52-b578-bc2f69f9927a' || results.analysisId === 'c54b2e33-3c47-4f03-918c-799bc701b82f' ? (
        // Custom SPIN analysis for specific meeting
        <div className="space-y-6">
          {/* Meeting Goals Assessment */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <CheckSquare className="w-6 h-6 text-blue-500" />
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                📋 Ocena realizacji celów spotkania
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Cel spotkania</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Realizacja</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-4 text-gray-800 dark:text-gray-200">1. Zbudowanie relacji</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                        ✅ Wykonane
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-4 text-gray-800 dark:text-gray-200">2. Analiza potrzeb</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                        ⚠️ Częściowe
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-4 text-gray-800 dark:text-gray-200">3. Prezentacja platformy</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                        ✅ Wykonane
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-800 dark:text-gray-200">4. Umówienie kolejnego spotkania</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                        ✅ Wykonane
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* SPIN Analysis */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <BarChart3 className="w-6 h-6 text-purple-500" />
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                📊 Szczegółowa analiza SPIN (w kontekście 1. spotkania)
              </h3>
            </div>

            <div className="space-y-6">
              {/* S - SITUATION */}
              <div>
                <h4 className="text-md font-bold text-gray-800 dark:text-gray-100 mb-3">S - SITUATION:</h4>
                
                <div className="mb-4">
                  <p className="font-semibold text-green-700 dark:text-green-300 mb-2">✅ Co było dobre:</p>
                  <div className="space-y-3 ml-4">
                    <div className="bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong>[00:49]</strong> "Jak Pani trafiła do tutlo?"</p>
                      <ul className="list-disc ml-5 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        <li>Doskonałe pytanie budujące relację i kontekst</li>
                        <li>Dało naturalny flow rozmowy</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong>[15:09]</strong> "Jak Pani się uczyła, to ile miała Pani czasu w tygodniu?"</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Kontekst doświadczenia klientki</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong>[20:34]</strong> "Ile mamy osób w firmie, dla ilu chcielibyśmy z tych osób dać dostęp?"</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Kluczowe pytanie sytuacyjne do przygotowania oferty</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong>[23:05]</strong> "Jeżeli chodzi o ilość minut, lekcji w tygodniu dla pracowników, jak Pani uważa, jaka liczba będzie odpowiednia?"</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Specyfikacja potrzeb</p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-950/20 border-l-4 border-blue-500 p-3 rounded-r-lg">
                      <p className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">Klientka SAMA dostarczyła dużo informacji sytuacyjnych:</p>
                      <ul className="list-disc ml-5 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        <li>Firma ma ~35 osób</li>
                        <li>Kończy się umowa z Profilingą końcem miesiąca</li>
                        <li>Angielski jest w celach firmowych</li>
                        <li>Różne działy (finanse, księgowość, budowa)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="font-semibold text-amber-700 dark:text-amber-300 mb-2">⚠️ Czego zabrakło (ale nie było krytyczne na tym etapie):</p>
                  <div className="ml-4">
                    <ul className="list-disc ml-5 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <li>Proces decyzyjny ("Kto podejmuje finalną decyzję?")</li>
                      <li>Budżet ("Jaki macie budżet?")</li>
                      <li>Timeline wdrożenia (choć wynika z końca umowy)</li>
                    </ul>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 italic">
                      Ocena w kontekście 1. spotkania: Wystarczające dla demo + booking, ale można było więcej.
                    </p>
                  </div>
                </div>
              </div>

              {/* P - PROBLEM */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-md font-bold text-gray-800 dark:text-gray-100 mb-3">P - PROBLEM:</h4>
                
                <div className="mb-4">
                  <p className="font-semibold text-green-700 dark:text-green-300 mb-2">✅ Co było dobre:</p>
                  <div className="space-y-3 ml-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300">Handlowiec dał przestrzeń klientce, która SAMA opowiedziała o problemach:</p>
                    <div className="bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong>[01:00-01:29]</strong> Problem personalny:</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 italic">"nie wykorzystałam wszystkich tych godzin, bo nigdy czasu nie było"</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong>[20:36-21:01]</strong> Problem z obecnym dostawcą:</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 italic">"bardzo mało ludzi korzystało z tej profilingu (...) to był podział tylko na dwie grupy i nie każdy czuł się komfortowo (...) chodziło może z 8 osób"</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1"><strong>[21:01-21:21]</strong> Decyzja o zmianie:</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 italic">"ja zarekomendowałam właśnie, żeby już nie przedłużać tej umowy, tylko żeby zrobić coś innego"</p>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                      W kontekście pierwszego spotkania: To jest wartościowe, bo klientka sama identyfikuje problemy bez nachalnych pytań.
                    </p>
                  </div>
                </div>

                <div>
                  <p className="font-semibold text-red-700 dark:text-red-300 mb-2">❌ Czego zabrakło (i TO JEST PROBLEM nawet na 1. spotkaniu):</p>
                  <div className="ml-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Brak JAKICHKOLWIEK pytań pogłębiających:</p>
                    <div className="space-y-2">
                      <div className="bg-red-50 dark:bg-red-950/20 border-l-4 border-red-500 p-3 rounded-r-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300">Klientka: "bardzo mało ludzi korzystało"</p>
                        <p className="text-sm text-red-600 dark:text-red-400">❌ Nie zapytał: "Dlaczego tak było?"</p>
                      </div>
                      <div className="bg-red-50 dark:bg-red-950/20 border-l-4 border-red-500 p-3 rounded-r-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300">Klientka: "nie każdy czuł się komfortowo"</p>
                        <p className="text-sm text-red-600 dark:text-red-400">❌ Nie zapytał: "Co dokładnie powodowało dyskomfort?"</p>
                      </div>
                      <div className="bg-red-50 dark:bg-red-950/20 border-l-4 border-red-500 p-3 rounded-r-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300">Klientka: "nie miałam czasu"</p>
                        <p className="text-sm text-red-600 dark:text-red-400">❌ Nie zapytał: "Co konkretnie blokowało?"</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-3 font-medium">
                      <strong>Ocena:</strong> Nawet w pierwszym spotkaniu warto zadać 2-3 pytania pogłębiające do każdego problemu, żeby:
                    </p>
                    <ul className="list-disc ml-5 text-sm text-gray-600 dark:text-gray-400 space-y-1 mt-1">
                      <li>Pokazać że słuchasz</li>
                      <li>Lepiej zrozumieć problem</li>
                      <li>Przygotować lepszą ofertę</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* I - IMPLICATION */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-md font-bold text-gray-800 dark:text-gray-100 mb-3">I - IMPLICATION:</h4>
                
                <div className="ml-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                    <strong>W kontekście pierwszego spotkania:</strong><br/>
                    Nie oczekuję głębokiej eksploracji implikacji na 1. spotkaniu, ale przynajmniej 1-2 pytania powinny paść:
                  </p>
                  
                  <div className="space-y-3">
                    <div className="bg-blue-50 dark:bg-blue-950/20 border-l-4 border-blue-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Po informacji o 8 z 35 osób:</p>
                      <ul className="list-none ml-3 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        <li>✅ "Ile to kosztowało firmę?"</li>
                        <li>✅ "Jak to wpływało na realizację celów firmowych?"</li>
                      </ul>
                    </div>
                    
                    <div className="bg-blue-50 dark:bg-blue-950/20 border-l-4 border-blue-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Po informacji o końcu umowy:</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">✅ "Co się stanie jeśli do końca miesiąca nie znajdziecie rozwiązania?"</p>
                    </div>
                  </div>

                  <div className="mt-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-2">Dlaczego to ważne nawet na 1. spotkaniu:</p>
                    <ul className="list-disc ml-5 text-sm text-amber-700 dark:text-amber-300 space-y-1">
                      <li>Buduje pilność ("kończy się miesiąc")</li>
                      <li>Pokazuje wagę problemu (koszty, cele)</li>
                      <li>Przygotowuje grunt pod ofertę (uzasadnia inwestycję)</li>
                    </ul>
                    <p className="text-sm text-amber-800 dark:text-amber-200 mt-2 font-medium">
                      Ocena: Nawet na 1. spotkaniu minimum 1-2 pytania implikacyjne są konieczne.
                    </p>
                  </div>
                </div>
              </div>

              {/* N - NEED-PAYOFF */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-md font-bold text-gray-800 dark:text-gray-100 mb-3">N - NEED-PAYOFF:</h4>
                
                <div className="ml-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                    <strong>W kontekście pierwszego spotkania:</strong><br/>
                    Nie oczekuję pełnej eksploracji Need-Payoff na demo call, ale jedno pytanie na zakończenie demo byłoby idealne:
                  </p>
                  
                  <div className="space-y-3">
                    <div className="bg-purple-50 dark:bg-purple-950/20 border-l-4 border-purple-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">Po pokazaniu platformy [~15:00]:</p>
                      <p className="text-sm text-purple-700 dark:text-purple-300 font-medium">
                        ✅ POWINIEN zapytać: "Jak Pani myśli, jak taka elastyczność (24/7, indywidualne sesje) wpłynęłaby na adopcję w Państwa firmie?"
                      </p>
                    </div>
                    
                    <div className="bg-purple-50 dark:bg-purple-950/20 border-l-4 border-purple-500 p-3 rounded-r-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">Albo:</p>
                      <p className="text-sm text-purple-700 dark:text-purple-300 font-medium">
                        ✅ "Co by to znaczyło dla realizacji celów firmowych, gdyby wszyscy 35 pracowników aktywnie korzystało?"
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-800 dark:text-green-200 mb-2">Dlaczego to ważne:</p>
                    <ul className="list-disc ml-5 text-sm text-green-700 dark:text-green-300 space-y-1">
                      <li>Klientka sama artykułuje wartość</li>
                      <li>To przygotowuje grunt pod ofertę</li>
                      <li>To buduje zaangażowanie</li>
                    </ul>
                    <p className="text-sm text-green-800 dark:text-green-200 mt-2 font-medium">
                      Ocena: Jedno pytanie Need-Payoff na koniec demo = must have.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Default Strengths & Improvements Grid
        <div className="grid md:grid-cols-2 gap-6">
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <TrendingUp className="w-6 h-6 text-green-500" />
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Mocne strony
              </h3>
            </div>
            <div className="space-y-3">
              {(results.feedback?.strengthsAnalysis?.strengths || []).map((strength, index) => (
                <div key={index} className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                  <p className="text-green-800 dark:text-green-200 font-medium">
                    {strength.feedback}
                  </p>
                  <div className="mt-3 p-3 bg-green-100 dark:bg-green-900/30 border-l-4 border-green-500 rounded-r-lg">
                    <p className="text-green-700 dark:text-green-300 text-sm font-medium italic">
                      &quot;{strength.quote}&quot;
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Obszary do poprawy
              </h3>
            </div>
            <div className="space-y-3">
              {(results.feedback?.improvementAnalysis?.improvements || []).map((improvement, index) => (
                <div key={index} className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                  <p className="text-amber-800 dark:text-amber-200 font-medium">
                    {improvement.feedback}
                  </p>
                  <div className="mt-3 p-3 bg-amber-100 dark:bg-amber-900/30 border-l-4 border-amber-500 rounded-r-lg">
                    <p className="text-amber-700 dark:text-amber-300 text-sm font-medium italic">
                      &quot;{improvement.quote}&quot;
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      {/*{results.feedback?.keyMetrics && (*/}
      {/*  <div className="glass-card rounded-2xl p-6">*/}
      {/*    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">*/}
      {/*      Kluczowe metryki*/}
      {/*    </h3>*/}
      {/*    <div className="grid grid-cols-3 gap-4">*/}
      {/*      <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20 rounded-xl">*/}
      {/*        <p className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-1">Pytania</p>*/}
      {/*        <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">*/}
      {/*          {results.feedback.keyMetrics.questionFrequency || 'N/A'}*/}
      {/*        </p>*/}
      {/*      </div>*/}
      {/*      <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20 rounded-xl">*/}
      {/*        <p className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">Pozytywność</p>*/}
      {/*        <p className="text-2xl font-bold text-green-800 dark:text-green-200">*/}
      {/*          {results.feedback.keyMetrics.positiveLanguage || 'N/A'}*/}
      {/*        </p>*/}
      {/*      </div>*/}
      {/*      <div className="text-center p-4 bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-950/20 dark:to-teal-900/20 rounded-xl">*/}
      {/*        <p className="text-sm text-teal-600 dark:text-teal-400 font-medium mb-1">Dopasowanie etapu</p>*/}
      {/*        <p className="text-2xl font-bold text-teal-800 dark:text-teal-200">*/}
      {/*          {results.feedback.keyMetrics.salesStageAlignment || 'N/A'}*/}
      {/*        </p>*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*)}*/}
    </div>
  );

  const renderSummaryTab = () => (
    <div className="space-y-6">
      {/* Duration & Audio Metrics */}
      <div className="glass-card rounded-2xl p-6">
        {results.metadata?.audio ? (
          <div className="space-y-4">
            {/* Duration from audio metadata */}
            <div className="flex items-center space-x-3">
              <Clock className="w-6 h-6 text-primary-500" />
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Czas trwania rozmowy: {results.metadata.audio.audioDuration || 'N/A'}
              </h3>
            </div>

            {/* Talk Ratio Visualization */}
            {results.metadata.audio.talkRatio && (
              <div className="mt-4">
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Udział w rozmowie:
                </h4>
                <div className="space-y-3">
                  {Object.entries(results.metadata.audio.talkRatio).map(([speaker, ratio], index) => {
                    const speakerInfo = getSpeakerInfo(speaker, index, Object.keys(results.metadata.audio.talkRatio).length);
                    return (
                      <div key={speaker} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            {speakerInfo.icon} {speakerInfo.displayName}
                          </span>
                          <span className="font-medium text-gray-800 dark:text-gray-200">
                            {Math.round(ratio * 100)}%
                          </span>
                        </div>
                        <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-1000 ${speakerInfo.color}`}
                            style={{ width: `${ratio * 100}%` }}
                          />
                        </div>
                        {results.metadata.audio.talkTime && results.metadata.audio.talkTime[speaker] && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Czas mówienia: {(() => {
                              const totalSeconds = Math.round(results.metadata.audio.talkTime[speaker]);
                              const minutes = Math.floor(totalSeconds / 60);
                              const seconds = totalSeconds % 60;
                              return minutes > 0 ? `${minutes}min ${seconds}s` : `${seconds}s`;
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <Clock className="w-6 h-6 text-primary-500" />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Czas trwania spotkania: {results.summary?.duration || 'N/A'} minut
            </h3>
          </div>
        )}
      </div>

      {/* Compact Summary Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Key Topics */}
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Kluczowe tematy omawiane
            </h3>
            <div className="space-y-3">
              {(results.summary?.keyTopics || []).map((topic, index) => (
                  <div key={index} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-3">
                    <p className="text-gray-800 dark:text-gray-200 font-medium">{topic}</p>
                  </div>
                ))}
            </div>
          </div>

          {/* Decisions Made */}
          {(results.summary?.decisions || []).length > 0 && (
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                Decyzje podjęte
              </h3>
              <div className="space-y-4">
                {(results.summary.decisions || []).map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1">✓</div>
                    {typeof item === 'object' && item !== null ? (
                      <div>
                        <p className="text-gray-800 dark:text-gray-200 font-medium">{item.decision}</p>
                        <p className="text-gray-500 dark:text-gray-400 text-xs">Odpowiedzialny: {item.responsible}</p>
                        <p className="text-gray-500 dark:text-gray-400 text-xs italic">Uzasadnienie: {item.rationale}</p>
                      </div>
                    ) : (
                      <p className="text-gray-700 dark:text-gray-300 text-sm">{item}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Open Questions */}
          {(results.summary?.openQuestions || []).length > 0 && (
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                Pytania otwarte/do rozstrzygnięcia
              </h3>
              <div className="space-y-4">
                {(results.summary.openQuestions || []).map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1">?</div>
                    {typeof item === 'object' && item !== null ? (
                      <div>
                        <p className="text-gray-800 dark:text-gray-200 font-medium">{item.question}</p>
                        <p className="text-gray-500 dark:text-gray-400 text-xs">Kontekst: {item.context}</p>
                      </div>
                    ) : (
                      <p className="text-gray-700 dark:text-gray-300 text-sm">{item}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Ustalenia dotyczące kolejnych kroków
            </h3>
            <div className="space-y-4">
              {(results.summary?.nextSteps || []).map((item, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-secondary-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1">{index + 1}</div>
                  {typeof item === 'object' && item !== null ? (
                    <div>
                      <p className="text-gray-800 dark:text-gray-200 font-medium">{item.step}</p>
                      <p className="text-gray-500 dark:text-gray-400 text-xs">Termin: {item.deadline} | Odpowiedzialny: {item.responsible}</p>
                    </div>
                  ) : (
                    <p className="text-gray-700 dark:text-gray-300 text-sm">{item}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Additional Notes */}
          {(results.summary?.additionalNotes || []).length > 0 && (
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                Dodatkowe uwagi
              </h3>
              <div className="space-y-4">
                {(results.summary.additionalNotes || []).map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    {typeof item === 'object' && item !== null ? (
                      <div>
                        <p className="text-gray-800 dark:text-gray-200 font-medium">{item.note}</p>
                        <p className="text-gray-500 dark:text-gray-400 text-xs">Kontekst: {item.context}</p>
                      </div>
                    ) : (
                      <p className="text-gray-700 dark:text-gray-300 text-sm">{item}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderTodoTab = () => (
    <div className="space-y-6">
      <div className="glass-card rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-6">
          Lista zadań z rozmowy
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Zadanie</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Odpowiedzialny</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Termin</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Priorytet</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Kategoria</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Status</th>
              </tr>
            </thead>
            <tbody>
              {(results.todoList?.actionItems || []).map((item, index) => (
                <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-4 px-4">
                    <div className="flex items-start space-x-2">
                      {item.contextInferred && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-950/20 dark:text-blue-400 mt-1">
                          wniosek z kontekstu
                        </span>
                      )}
                      <p className="text-gray-800 dark:text-gray-200 font-medium">{item.task}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-300">{item.assignee || item.responsible || 'Do ustalenia'}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-gray-600 dark:text-gray-300 text-sm">
                      {item.deadline || 'termin do ustalenia'}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    {(() => {
                      const priorityMapping = getPriorityMapping(item.priority);
                      return (
                        <span 
                          className={clsx(
                            'px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap',
                            priorityMapping.color === 'red' && 'bg-red-100 text-red-800 dark:bg-red-950/20 dark:text-red-400',
                            priorityMapping.color === 'amber' && 'bg-amber-100 text-amber-800 dark:bg-amber-950/20 dark:text-amber-400',
                            priorityMapping.color === 'orange' && 'bg-orange-100 text-orange-800 dark:bg-orange-950/20 dark:text-orange-400',
                            priorityMapping.color === 'gray' && 'bg-gray-100 text-gray-800 dark:bg-gray-950/20 dark:text-gray-400',
                            priorityMapping.color === 'green' && 'bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-400'
                          )}
                          title={priorityMapping.description}
                        >
                          {priorityMapping.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="py-4 px-4">
                    {item.category ? (
                      (() => {
                        const categoryMapping = getCategoryMapping(item.category);
                        return (
                          <span 
                            className={clsx(
                              'px-2 py-1 rounded-lg text-xs font-medium',
                              categoryMapping.color === 'purple' && 'bg-purple-100 text-purple-800 dark:bg-purple-950/20 dark:text-purple-400',
                              categoryMapping.color === 'indigo' && 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/20 dark:text-indigo-400',
                              categoryMapping.color === 'cyan' && 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950/20 dark:text-cyan-400',
                              categoryMapping.color === 'green' && 'bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-400',
                              categoryMapping.color === 'teal' && 'bg-teal-100 text-teal-800 dark:bg-teal-950/20 dark:text-teal-400'
                            )}
                            title={categoryMapping.description}
                          >
                            {categoryMapping.label}
                          </span>
                        );
                      })()
                    ) : (
                      <span className="text-gray-400 text-xs">-</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional Notes about Dependencies */}
      {results.todoList?.notes && (
        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Uwagi o zadaniach
          </h3>
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <p className="text-blue-800 dark:text-blue-200 text-sm leading-relaxed">
              {results.todoList.notes}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderEmailTab = () => (
    <div className="space-y-6">
      {/* Email Draft */}
      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Szkic emaila follow-up
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => copyToClipboard(results.email?.content || '')}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 
                         text-gray-700 dark:text-gray-300 rounded-lg transition-colors duration-200"
            >
              <Copy className="w-4 h-4" />
              <span>Kopiuj</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 btn-primary">
              <ExternalLink className="w-4 h-4" />
              <span>Otwórz w emailu</span>
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Temat</label>
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
              <p className="text-gray-800 dark:text-gray-200 font-medium">{results.email?.subject || 'Brak tematu'}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Treść</label>
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-sans leading-relaxed">
                {results.email?.content || 'Brak treści emaila'}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Key Elements (Enhanced) */}
      {results.email?.keyElements && (
        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Kluczowe elementy
          </h3>
          <div className="space-y-4">
            <div className="bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4">
              <h4 className="font-semibold text-indigo-800 dark:text-indigo-200 mb-2">Główne referencje z rozmowy</h4>
              <p className="text-indigo-700 dark:text-indigo-300 text-sm leading-relaxed">
                {results.email.keyElements.mainReferences}
              </p>
            </div>
            <div className="bg-cyan-50 dark:bg-cyan-950/20 border border-cyan-200 dark:border-cyan-800 rounded-xl p-4">
              <h4 className="font-semibold text-cyan-800 dark:text-cyan-200 mb-2">Uzasadnienie tonu</h4>
              <p className="text-cyan-700 dark:text-cyan-300 text-sm leading-relaxed">
                {results.email.keyElements.toneJustification}
              </p>
            </div>
            <div className="bg-teal-50 dark:bg-teal-950/20 border border-teal-200 dark:border-teal-800 rounded-xl p-4">
              <h4 className="font-semibold text-teal-800 dark:text-teal-200 mb-2">Strategiczny focus</h4>
              <p className="text-teal-700 dark:text-teal-300 text-sm leading-relaxed">
                {results.email.keyElements.strategicFocus}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Communication Analysis (Enhanced) */}
      {results.email?.communicationAnalysis && (
        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Analiza komunikacji
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Poziom formalności</h4>
              <p className="text-blue-700 dark:text-blue-300" title={getFormalityMapping(results.email.communicationAnalysis.formalityLevel).description}>
                {getFormalityMapping(results.email.communicationAnalysis.formalityLevel).label}
              </p>
            </div>
            <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
              <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">Styl relacji</h4>
              <p className="text-green-700 dark:text-green-300" title={getRelationshipMapping(results.email.communicationAnalysis.relationshipStyle).description}>
                {getRelationshipMapping(results.email.communicationAnalysis.relationshipStyle).label}
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
              <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">Etap sprzedaży</h4>
              <p className="text-purple-700 dark:text-purple-300" title={emailSalesStageMapping.description}>
                {emailSalesStageMapping.label}
              </p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Ton do zastosowania</h4>
              <p className="text-amber-700 dark:text-amber-300" title={getToneMapping(results.email.communicationAnalysis.toneToUse).description}>
                {getToneMapping(results.email.communicationAnalysis.toneToUse).label}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderTranscriptTab = () => {
    const transcript = results.transcript;

    const getSourceLabel = (source?: string) => {
      switch (source) {
        case 'manual': return '✏️ Ręcznie wprowadzony';
        case 'pdf': return '📄 Plik PDF';
        case 'txt': return '📝 Plik tekstowy';
        case 'audio_api': return '🎤 Audio przepisane przez AI';
        case 'audio_file': return '🎵 Przesłany plik audio';
        default: return '📄 Źródło tekstowe';
      }
    };

    return (
      <div className="space-y-6">
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">
                Oryginalny transkrypt
              </h3>
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                <span>{getSourceLabel(transcript?.source)}</span>
                <span>•</span>
                <span>{transcript?.fullLength.toLocaleString() || 0} znaków</span>
                {transcript?.filename && (
                  <>
                    <span>•</span>
                    <span>{transcript.filename}</span>
                  </>
                )}
                {transcript?.audioUrl && (
                  <>
                    <span>•</span>
                    <span>{transcript.numSpeakers} mówców</span>
                  </>
                )}
                <span>•</span>
                <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">
                  ID: {transcript?.sessionId?.slice(-6)}
                </span>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={copyFullTranscript}
                disabled={loadingTranscript}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-colors duration-200 disabled:opacity-50 ${
                  transcriptCopied
                    ? 'bg-green-100 hover:bg-green-200 dark:bg-green-900/20 dark:hover:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'bg-primary-100 hover:bg-primary-200 dark:bg-primary-900 dark:hover:bg-primary-800 text-primary-700 dark:text-primary-300'
                }`}
                title="Kopiuj cały transkrypt do schowka"
              >
                <Copy className="w-4 h-4" />
                <span>
                  {loadingTranscript
                    ? 'Ładowanie...'
                    : transcriptCopied
                    ? 'Skopiowano!'
                    : 'Kopiuj do schowka'
                  }
                </span>
              </button>
            </div>
          </div>

          {transcript?.audioUrl && (
            <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-xl">
              <div className="flex items-center space-x-2 mb-2">
                <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <span className="font-medium text-purple-800 dark:text-purple-200">Szczegóły transkrypcji AI</span>
              </div>
              <div className="space-y-1 text-sm text-purple-700 dark:text-purple-300">
                <p><strong>Źródłowy URL:</strong> {transcript.audioUrl}</p>
                <p><strong>Mówcy:</strong> {transcript.numSpeakers}</p>
                <p><strong>Usługa:</strong> Speech-to-Text API</p>
              </div>
            </div>
          )}

          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            {loadingTranscript ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Ładowanie pełnego transkryptu...</span>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-sans leading-relaxed text-sm">
                  {fullTranscript || transcript?.preview || 'Transkrypt niedostępny'}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAdditionalDataTab = () => {
    const hasData = results.crmMappings || results.successCriterias || results.agendaScript || results.processMapping;

    if (!hasData) {
      return (
        <div className="glass-card rounded-2xl p-6">
          <p className="text-gray-600 dark:text-gray-400">Brak dodatkowych danych z n8n.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* CRM Mappings */}
        {results.crmMappings && results.crmMappings.length > 0 && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Mapowanie CRM
            </h3>
            <div className="space-y-4">
              {results.crmMappings.map((mapping, index) => (
                <div key={index} className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                  <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                    {mapping.crmField}
                  </h4>
                  <p className="text-blue-700 dark:text-blue-300 text-sm whitespace-pre-wrap">
                    {mapping.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Success Criteria */}
        {results.successCriterias && results.successCriterias.length > 0 && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Kryteria sukcesu
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Kryterium</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Spełnione</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Cytat</th>
                  </tr>
                </thead>
                <tbody>
                  {results.successCriterias.map((criteria, index) => (
                    <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-4 px-4">
                        <p className="text-gray-800 dark:text-gray-200 font-medium">
                          {criteria.successCriteria}
                        </p>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={clsx(
                            'px-3 py-1 rounded-full text-xs font-medium',
                            criteria.fulfilled === 'true'
                              ? 'bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-950/20 dark:text-red-400'
                          )}
                        >
                          {criteria.fulfilled === 'true' ? '✓ Tak' : '✗ Nie'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-gray-600 dark:text-gray-300 text-sm italic">
                          &quot;{criteria.quota}&quot;
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Agenda Script */}
        {results.agendaScript && results.agendaScript.length > 0 && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Scenariusz Agendy
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Element</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Spełnione</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200">Cytat</th>
                  </tr>
                </thead>
                <tbody>
                  {results.agendaScript.map((element, index) => (
                    <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-4 px-4">
                        <p className="text-gray-800 dark:text-gray-200 font-medium">
                          {element.agendaScriptElement}
                        </p>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={clsx(
                            'px-3 py-1 rounded-full text-xs font-medium',
                            element.fulfilled === 'true'
                              ? 'bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-950/20 dark:text-red-400'
                          )}
                        >
                          {element.fulfilled === 'true' ? '✓ Tak' : '✗ Nie'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-gray-600 dark:text-gray-300 text-sm italic">
                          &quot;{element.quota}&quot;
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Process Mapping - Hidden for now */}
        {false && results.processMapping && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Mapowanie Procesów Biznesowych
            </h3>

            <div className="space-y-4">
              {/* Status and Confidence */}
              <div className="flex items-center space-x-4">
                <span
                  className={clsx(
                    'px-3 py-1 rounded-full text-xs font-medium',
                    results.processMapping.found
                      ? 'bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-950/20 dark:text-gray-400'
                  )}
                >
                  {results.processMapping.found ? '✓ Znaleziono' : 'Nie znaleziono'}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Pewność: <strong className="text-gray-800 dark:text-gray-200">{results.processMapping.confidence}</strong>
                </span>
              </div>

              {/* Processes */}
              {results.processMapping.processes.sales_process && (
                <div className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                  <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-3">
                    Proces Sprzedaży
                  </h4>
                  <ol className="list-decimal list-inside space-y-1 text-purple-700 dark:text-purple-300 text-sm">
                    {results.processMapping.processes.sales_process.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {results.processMapping.processes.customer_service && (
                <div className="bg-cyan-50 dark:bg-cyan-950/20 border border-cyan-200 dark:border-cyan-800 rounded-xl p-4">
                  <h4 className="font-semibold text-cyan-800 dark:text-cyan-200 mb-3">
                    Obsługa Klienta
                  </h4>
                  <ol className="list-decimal list-inside space-y-1 text-cyan-700 dark:text-cyan-300 text-sm">
                    {results.processMapping.processes.customer_service.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {results.processMapping.processes.methodology && (
                <div className="bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4">
                  <h4 className="font-semibold text-indigo-800 dark:text-indigo-200 mb-2">
                    Metodologia
                  </h4>
                  <p className="text-indigo-700 dark:text-indigo-300 text-sm">
                    {results.processMapping.processes.methodology}
                  </p>
                </div>
              )}

              {/* Source Fragments */}
              {results.processMapping.sourceFragments && results.processMapping.sourceFragments.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">
                    Fragmenty źródłowe
                  </h4>
                  <div className="space-y-2">
                    {results.processMapping.sourceFragments.map((fragment, index) => (
                      <div key={index} className="p-3 bg-white dark:bg-gray-900 rounded-lg border-l-4 border-gray-400">
                        <p className="text-gray-700 dark:text-gray-300 text-sm italic">
                          &quot;{fragment}&quot;
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    if (!results) {
      return <div>Brak wyników do wyświetlenia.</div>;
    }
    switch (activeTab) {
      case 'feedback':
        return results.feedback ? renderFeedbackTab() : <div>Brak analizy feedbacku.</div>;
      case 'summary':
        return results.summary ? renderSummaryTab() : <div>Brak podsumowania.</div>;
      case 'todo':
        return results.todoList ? renderTodoTab() : <div>Brak listy zadań.</div>;
      case 'email':
        return results.email ? renderEmailTab() : <div>Brak szkicu emaila.</div>;
      case 'transcript':
        return results.transcript ? renderTranscriptTab() : <div>Brak transkryptu.</div>;
      case 'additional':
        return renderAdditionalDataTab();
      default:
        return results.feedback ? renderFeedbackTab() : <div>Brak danych.</div>;
    }
  };

  return (
    <div className="animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold gradient-text mb-2">Wyniki Analizy</h2>
          {results?.title && (
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
              {results.title}
            </h3>
          )}
          {results?.metadata && (
            <p className="text-gray-600 dark:text-gray-300">
              {results.metadata.audio?.audioDuration
                ? `Czas trwania rozmowy: ${results.metadata.audio.audioDuration}`
                : results.summary?.duration
                  ? `Czas trwania rozmowy: ${results.summary.duration} min`
                  : 'Czas trwania: N/A'
              } • {salesStageMapping.label}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {results.analysisId && (
            <button
              onClick={copyPermalink}
              className={`flex items-center space-x-2 px-4 py-3 rounded-xl transition-colors duration-200 ${
                linkCopied 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' 
                  : 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400'
              }`}
              title="Kopiuj link do analizy"
            >
              {linkCopied ? <Copy className="w-5 h-5" /> : <ExternalLink className="w-5 h-5" />}
              <span>{linkCopied ? 'Skopiowano!' : 'Share'}</span>
            </button>
          )}
          <button
            onClick={onReset}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 
                       text-gray-700 dark:text-gray-300 rounded-xl transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Nowa Analiza</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-8">
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={clsx(
                'flex items-center space-x-2 px-3 sm:px-6 py-3 rounded-xl transition-all duration-200 whitespace-nowrap',
                activeTab === id
                  ? 'bg-white dark:bg-gray-700 shadow-lg text-primary-600 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
              )}
              title={label}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="animate-fade-in">
        {renderTabContent()}
      </div>
    </div>
  );
};

